import {Component, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Attribute, Club, TicketQuota} from '~core/models';
import {TixxSelectTranslationsService} from '~shared/translations';
import {NotificationType} from '~core/enum';
import {AttributeService, DefaultNotificationService, StorageService, TicketQuotaService} from '~core/services';
import {FormLabelTranslationService} from '~shared/components/form-fields/translations/form-label-translations.service';
import {SpinnerService} from '~shared/components/buttons/spinner/spinner.service';
import {ButtonsTranslations} from '~shared/components/buttons/buttons-translations.service';
import {EventAdministrationService} from "~features/tixx-select/event-administration.service";

/**
 * Workspace sidebar for editing ticket quota data
 */
@UntilDestroy()
@Component({
  selector: 'app-ticket-quota',
  templateUrl: './ticket-quota.component.html',
})
export class TicketQuotaComponent implements OnInit, OnDestroy {
  public ticketQuota: TicketQuota;
  public ticketQuotaForm: UntypedFormGroup;
  public spinnerName: string = 'ticketQuotaSubmit';
  public yesOrNoDropdown: Array<any> = [];
  public clubAttributes: Array<Attribute>;
  public loading: boolean;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private storageService: StorageService,
    private notificationService: DefaultNotificationService,
    private ticketQuotaService: TicketQuotaService,
    private attributeService: AttributeService,
    public tixxSelectOverviewService: EventAdministrationService,
    public tixxSelectTranslationService: TixxSelectTranslationsService,
    public flTranslations: FormLabelTranslationService,
    public buttonsTranslations: ButtonsTranslations,
    public spinnerService: SpinnerService,
  ) {
    this.attributeService.castClubAttributes.pipe(untilDestroyed(this))
      .subscribe((data: Array<Attribute>) => {
        if (data) {
          this.clubAttributes = data;
        }
      });
    this.storageService.castSelectedClub.pipe(untilDestroyed(this)).subscribe((data: Club) => {
      if (!this.clubAttributes) {
        this.loading = true;
        this.attributeService.getClubAttribute(data.id).pipe(untilDestroyed(this)).subscribe((res: any) => {
          this.attributeService.announceClubAttributes(res);
          this.loading = false;
        });
      }
    });
    this.ticketQuotaService.castCurrentTicketQuota.pipe(untilDestroyed(this))
      .subscribe((data: TicketQuota) => this.ticketQuota = data);
  }

  get modalStatus(): boolean {
    return this.tixxSelectOverviewService.listOfModals.editTicketQuotaModalOpened;
  }

  get attributes(): UntypedFormArray {
    return <UntypedFormArray>this.ticketQuotaForm.get('customerAttributeConstraintItems');
  }

  public ngOnInit(): void {
    this.yesOrNoDropdown.push(
      {value: true, status: this.flTranslations.i18nActivated},
      {value: false, status: this.flTranslations.i18nDeactivated},
    );
    this.fillForm();
  }

  public ngOnDestroy(): void {
    this.tixxSelectOverviewService.toggleEditTicketQuotaModal(false);
  }

  /**
   * Fill the data for the attributes.
   */
  public fillAttributes(event: string): void {
    const form = this.ticketQuotaForm.getRawValue();
    if (event === '0: true') {
      if (this.ticketQuota.customerAttributeConstraintItems.length !== 0) {
        for (const att of this.ticketQuota.customerAttributeConstraintItems) {
          const obj = {id: att.id, attributeName: att.attributeName, quota: att.quota};
          this.attributes.push(this.formBuilder.group(obj));
        }
      } else {
        for (const att of this.clubAttributes) {
          const obj = {attributeName: att.name, quota: form.generalQuota ? form.generalQuota : 0};
          this.attributes.push(this.formBuilder.group(obj));
        }
      }
    } else {
      this.attributes.clear();
    }
  }

  /**
   * Create the form.
   */
  public fillForm(): void {
    this.ticketQuotaForm = this.formBuilder.group({
      id: [this.ticketQuota.id],
      submissionProcessSetup: [this.ticketQuota.submissionProcessSetup],
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      generalQuotaPresent: this.ticketQuota.generalQuota ? true : false,
      generalQuota: [this.ticketQuota.generalQuota],
      // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
      activeMemberQuotaPresent: this.ticketQuota.activeMemberQuota ? true : false,
      activeMemberQuota: [this.ticketQuota.activeMemberQuota],
      customerAttributeConstraintItemsPresent: this.ticketQuota.customerAttributeConstraintItems.length === 0 ? false : true,
      customerAttributeConstraintItems: this.formBuilder.array([]),
    });
    for (const att of this.ticketQuota.customerAttributeConstraintItems) {
      const obj = {id: att.id, attributeName: att.attributeName, quota: att.quota};
      this.attributes.push(this.formBuilder.group(obj));
    }
  }

  public selectChanged(event: any, path: string): void {
    if (event === '0: true') {
      this.ticketQuotaForm.get(path).patchValue(1);
    } else {
      this.ticketQuotaForm.get(path).patchValue(null);
    }
  }

  /**
   * If form is valid update the process configuration, otherwise display the errors.
   */
  public updateFormSubmit(form: UntypedFormGroup): void {
    const savedForm = form.value;
    if (form.valid) {
      if (!savedForm.customerAttributeConstraintItemsPresent) {
        savedForm.customerAttributeConstraintItems = [];
      }
      delete savedForm.generalQuotaPresent;
      delete savedForm.activeMemberQuotaPresent;
      delete savedForm.customerAttributeConstraintItemsPresent;

      this.spinnerService.show(this.spinnerName);
      this.ticketQuotaService.update(savedForm).pipe(untilDestroyed(this))
        .subscribe({
          next: (data: any) => {
            this.ticketQuotaService.setCurrentTicketQuota(data);
            this.spinnerService.hide(this.spinnerName);
            this.tixxSelectOverviewService.toggleEditTicketQuotaModal(false);
            this.notificationService.showNotification({
              type: NotificationType.SUCCESS,
              message: this.tixxSelectTranslationService.i18nSuccessUpdateLottery
            });
            this.fillForm();
          },
          error: (error: Error) => {
            this.notificationService.showNotification({
              type: NotificationType.ALERT,
              message: this.tixxSelectTranslationService.i18nErrorUpdateLottery
            });
            this.spinnerService.hide(this.spinnerName);
          }
        });
    }
  }

}
